import { useCallback, useRef, useState } from 'react';
import {
  hasTrainingTranslationGaps,
  type TranslationTraining
} from './translationGaps';
import {
  isTrainingContentInSync,
  normalizeContentTimestamp
} from '../trainingContentTimestamps';
import type { EditorTraining } from '~/shell/Store';
import navigateWithFullPageReload from '~/lib/navigations/navigateWithFullPageReload';

type PublishTrainingParams = {
  should_auto_update_translations: boolean;
};

type PublishTrainingResponse = {
  redirectUrl?: string;
  error?: boolean;
};

type PublishResult = 'navigated' | 'refused' | 'no_destination';

const TRANSLATIONS_SUBMITTED_PARAM = 'auto_update_translations_submitted';

const withTranslationsSubmittedFlag = (
  destination: string,
  responseRedirectUrl?: string
) => {
  if (!responseRedirectUrl) return destination;

  const submitted = new URL(
    responseRedirectUrl,
    window.location.origin
  ).searchParams.get(TRANSLATIONS_SUBMITTED_PARAM);
  if (submitted !== 'true') return destination;

  const separator = destination.includes('?') ? '&' : '?';
  return `${destination}${separator}${TRANSLATIONS_SUBMITTED_PARAM}=true`;
};

type CourseInfoResponse = {
  error?: boolean;
  training?: TranslationTraining;
};

type CourseEditorApi = {
  publishTraining: (
    trainingId: number,
    params: PublishTrainingParams
  ) => Promise<PublishTrainingResponse>;
  getCourseInfo: (trainingId: number) => Promise<CourseInfoResponse>;
};

type UseExitPublishFlowParams = {
  apiService: CourseEditorApi | null;
  training: TranslationTraining | null | undefined;
  publishRedirectUrl?: string | null;
  exitRedirectUrl: string;
  shouldCheckTranslationsBeforeExit: boolean;
  hasTranslationGaps: boolean;
  setTraining: (training: TranslationTraining) => void;
  markTrainingPublished: () => void;
  setDisplayUpdateTranslationsModal: (display: boolean) => void;
  setShouldAutoUpdateTranslations: (
    shouldAutoUpdateTranslations: boolean
  ) => void;
  onError: () => void;
  onPublishError: () => void;
};

type OnExitOptions = {
  /**
   * Assign reuses this flow but must still publish/redirect via publish URL
   * even when content is unchanged. Only the X/close path may skip publish.
   */
  requirePublish?: boolean;
};

type PublishOptions = {
  notifyOnFailure?: boolean;
  showBusyState?: boolean;
};

type UseExitPublishFlowResult = {
  isExitInProgress: boolean;
  publish: (
    shouldAutoUpdateTranslations: boolean,
    options?: PublishOptions
  ) => Promise<void>;
  onExit: (options?: OnExitOptions) => Promise<void>;
};

export const useExitPublishFlow = ({
  apiService,
  training,
  publishRedirectUrl,
  exitRedirectUrl,
  shouldCheckTranslationsBeforeExit,
  hasTranslationGaps,
  setTraining,
  markTrainingPublished,
  setDisplayUpdateTranslationsModal,
  setShouldAutoUpdateTranslations,
  onError,
  onPublishError
}: UseExitPublishFlowParams): UseExitPublishFlowResult => {
  const [isExitInProgress, setIsExitInProgress] = useState(false);
  const exitInProgressRef = useRef(false);

  const doPublish = useCallback(
    async (shouldAutoUpdateTranslations: boolean): Promise<PublishResult> => {
      if (!apiService || training?.id == null) return 'no_destination';

      const publishResponse = await apiService.publishTraining(training.id, {
        should_auto_update_translations: shouldAutoUpdateTranslations
      });

      // The API has already shown why it refused; leaving would hide that and look like a publish.
      if (!publishResponse || publishResponse.error) return 'refused';

      markTrainingPublished();

      if (publishRedirectUrl) {
        navigateWithFullPageReload(
          withTranslationsSubmittedFlag(
            process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL +
              publishRedirectUrl,
            publishResponse.redirectUrl
          )
        );
        return 'navigated';
      }

      if (publishResponse.redirectUrl) {
        navigateWithFullPageReload(publishResponse.redirectUrl);
        return 'navigated';
      }

      return 'no_destination';
    },
    [apiService, markTrainingPublished, publishRedirectUrl, training]
  );

  const publish = useCallback(
    async (shouldAutoUpdateTranslations: boolean, options?: PublishOptions) => {
      if (exitInProgressRef.current) return;

      const notifyOnFailure = options?.notifyOnFailure ?? true;
      const showBusyState = options?.showBusyState ?? true;

      exitInProgressRef.current = true;
      if (showBusyState) setIsExitInProgress(true);

      let shouldResetExitInProgress = true;

      try {
        setShouldAutoUpdateTranslations(shouldAutoUpdateTranslations);

        const result = await doPublish(shouldAutoUpdateTranslations);
        if (result !== 'navigated') {
          if (result === 'no_destination' && notifyOnFailure) onPublishError();
          return;
        }

        setDisplayUpdateTranslationsModal(false);
        shouldResetExitInProgress = false;
      } catch {
        if (notifyOnFailure) onPublishError();
      } finally {
        if (shouldResetExitInProgress) {
          exitInProgressRef.current = false;
          setIsExitInProgress(false);
        }
      }
    },
    [
      doPublish,
      onPublishError,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations
    ]
  );

  const onExit = useCallback(
    async (options?: OnExitOptions) => {
      if (exitInProgressRef.current) return;

      exitInProgressRef.current = true;

      let shouldResetExitInProgress = true;

      try {
        if (shouldCheckTranslationsBeforeExit) {
          if (hasTranslationGaps) {
            setShouldAutoUpdateTranslations(false);
            setDisplayUpdateTranslationsModal(true);
            return;
          }

          if (!apiService || training?.id == null) {
            onError();
            return;
          }

          const latestCourse = await apiService.getCourseInfo(training.id);
          if (latestCourse?.error || !latestCourse?.training) {
            onError();
            return;
          }

          const latestHasTranslationGaps = hasTrainingTranslationGaps(
            latestCourse.training
          );

          if (latestHasTranslationGaps) {
            setTraining(latestCourse.training);
            setShouldAutoUpdateTranslations(false);
            setDisplayUpdateTranslationsModal(true);
            return;
          }
        }

        // Close (X): skip publish-on-exit when live content is unchanged so we
        // don't flash "Saving..." with nothing to save (CAT-677).
        // Assign: always publish — it must use publishRedirectUrl / API redirect.
        // If timestamps are missing, keep the legacy publish-on-exit behavior.
        if (training?.status === 'live') {
          const editorTraining = training as EditorTraining;
          const hasKnownContentTimestamps =
            normalizeContentTimestamp(editorTraining.contentEditedAt) != null ||
            normalizeContentTimestamp(editorTraining.lastPublishedAt) != null;
          const shouldSkipPublish =
            !options?.requirePublish &&
            hasKnownContentTimestamps &&
            isTrainingContentInSync(editorTraining);

          if (!shouldSkipPublish) {
            setIsExitInProgress(true);
            const result = await doPublish(false);
            if (result !== 'navigated') {
              if (result === 'no_destination') onPublishError();
              return;
            }

            shouldResetExitInProgress = false;
            return;
          }
        }

        if (!window || !exitRedirectUrl) {
          return;
        }

        const resultRedirectUrl =
          process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + exitRedirectUrl;

        if (window.parent && window.self !== window.parent) {
          window.parent.location.href = resultRedirectUrl;
        } else {
          window.location.href = resultRedirectUrl;
        }

        shouldResetExitInProgress = false;
      } catch {
        onError();
      } finally {
        if (shouldResetExitInProgress) {
          exitInProgressRef.current = false;
          setIsExitInProgress(false);
        }
      }
    },
    [
      apiService,
      doPublish,
      exitRedirectUrl,
      hasTranslationGaps,
      onError,
      onPublishError,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations,
      setTraining,
      shouldCheckTranslationsBeforeExit,
      training
    ]
  );

  return {
    isExitInProgress,
    publish,
    onExit
  };
};
